import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import type { FormulatorToolsetBridge } from "@ttc-spectree/toolbox-contract";
import { AdoPatCallout } from "@/components/ado/ado-pat-callout";
import { FormulatorInterview } from "@/components/formulator/formulator-interview";
import { FormulatorNotice } from "@/components/formulator/formulator-notice";
import { FormulatorReview } from "@/components/formulator/formulator-review";
import { FormulatorStart } from "@/components/formulator/formulator-start";
import { Chip } from "@/components/grooming/grooming-chips";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { Button } from "@/components/ui/button";
import { useWorkItemInterview } from "@/hooks/queries/use-work-item-interview";
import { usePageTitle } from "@/hooks/use-page-title";
import { getApiErrorCode, getAdoPatErrorCode } from "@/lib/api/work-items";
import { formulatorImprovePath, formulatorSessionPath } from "@/lib/api/work-item-interview";
import { useToolboxBridge, useToolsetSupport } from "@/lib/toolbox/bridge";
import {
  DEFAULT_FORMULATOR_ACTIVITY,
  formulatorErrorMessage,
  formulatorOpeningTurnMessage,
  useFormulatorEvents,
  type FormulatorErrorMessage,
} from "@/lib/toolbox/formulator";
import { FORMULATOR_PATH, TOOLBOX_HOME_PATH } from "@/lib/toolbox/tools";
import { SUPPORT_LABELS } from "@/pages/toolbox/home";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";

/**
 * `/toolbox/formulator`: the Toolbox Formulator (epic PI-SPEC-SW-E282,
 * feature PI-SPEC-SW-1418; Claude Design "SpecTree Grooming Desktop", Write
 * view). Someone writes a user story or bug that meets the grooming bar:
 * Claude Code reads the project's code on their computer, asks what is
 * missing, and the item is created (or an existing one improved) in ADO
 * only when they choose to.
 *
 * - `?session=<id>` resumes a session (start and improve land here too);
 * - `?mode=improve&project=<p>&id=<n>` starts improving an existing item;
 * - otherwise the start view.
 *
 * Starts, replies and redrafts go through `window.spectreeToolbox.formulator`
 * (the turns run on the person's Claude seat; `redraft` only when the app
 * offers it, epic PI-SPEC-SW-E321); the review gets the session's turn state
 * so a draft turn that started during the interview shows as Claude writing
 * the text; the session, the review and the
 * submit go straight to the SpecTree API. In a plain browser the page
 * explains that the Formulator runs in SpecTree Toolbox. Routed behind
 * `RequireAdoPat`.
 */

const ITEM_ID_PATTERN = /^[1-9]\d{0,9}$/;

/** Navigation state set when this page started the session, so it never resumes a turn already running here. */
interface FormulatorLocationState {
  startedHere?: boolean;
}

function Page({ children }: { children: ReactNode }) {
  return <div className="flex min-h-full flex-1 flex-col">{children}</div>;
}

function Centered({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-1 items-center justify-center p-8">
      <div className="flex w-full max-w-[640px] flex-col gap-4">{children}</div>
    </div>
  );
}

function Waiting({ label, testId }: { label: string; testId?: string }) {
  return (
    <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground" role="status" data-testid={testId}>
      <span
        className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-primary border-r-transparent"
        aria-hidden="true"
      />
      {label}
    </div>
  );
}

/** The app cannot run the Formulator: the same words as the Formulator tile on the Toolbox home. */
function UpdateNeeded({ support }: { support: "app-update-needed" | "spectree-update-needed" }) {
  const label = SUPPORT_LABELS[support];
  return (
    <Centered>
      <div className="flex flex-col gap-3" data-testid="formulator-update-needed">
        <h1 className={TOOLBOX_PAGE_TITLE}>ADO Formulator</h1>
        <div>
          <Chip tone={label.tone}>{label.text}</Chip>
        </div>
        <Link to={TOOLBOX_HOME_PATH} className="text-[13px] text-link hover:underline">
          Back to SpecTree Toolbox
        </Link>
      </div>
    </Centered>
  );
}

// ---------------------------------------------------------------------------
// Improve: start straight from a link
// ---------------------------------------------------------------------------

/**
 * `?mode=improve&project=&id=`: start the improve session at once (the item
 * already says what to ask), then land on `?session=`.
 */
function ImproveStarter({
  formulator,
  project,
  id,
  onStarted,
}: {
  formulator: FormulatorToolsetBridge;
  project: string;
  id: string;
  onStarted: (sessionId: string) => void;
}) {
  const turn = useFormulatorEvents(null);
  const [failure, setFailure] = useState<FormulatorErrorMessage | null>(null);
  const [attempt, setAttempt] = useState(0);
  const startedAttempt = useRef<number | null>(null);

  useEffect(() => {
    if (startedAttempt.current === attempt) return;
    startedAttempt.current = attempt;
    setFailure(null);
    void formulator
      .start({ project, itemId: id })
      .then((result) => {
        if (result.ok) onStarted(result.sessionId);
        // No answer typed yet: NO_TURN_RETURNED says "first question", not "Your answer is kept".
        else setFailure(formulatorOpeningTurnMessage(formulatorErrorMessage(result.errorCode, result.message)));
      })
      .catch(() => {
        setFailure(formulatorErrorMessage("UNKNOWN"));
      });
  }, [attempt, formulator, id, onStarted, project]);

  return (
    <Centered>
      <div className="flex flex-col gap-1.5">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Improve #{id}
        </span>
        <h1 className={TOOLBOX_PAGE_TITLE}>Getting the item ready</h1>
        <p className="text-muted-foreground">
          Claude reads {project}'s code on this computer before it asks what the item is missing.
        </p>
      </div>
      {failure ? (
        <FormulatorNotice message={failure} testId="improve-start-error">
          <button
            type="button"
            className="font-medium text-link hover:underline"
            onClick={() => {
              setAttempt((n) => n + 1);
            }}
          >
            Try again
          </button>
          <Link to={FORMULATOR_PATH} className="text-muted-foreground hover:text-foreground">
            Write something else
          </Link>
        </FormulatorNotice>
      ) : (
        <Waiting label={turn.activity ?? "Starting the interview"} testId="improve-starting" />
      )}
    </Centered>
  );
}

// ---------------------------------------------------------------------------
// One session: interview, then review and create
// ---------------------------------------------------------------------------

function FormulatorSession({
  formulator,
  sessionId,
  startedHere,
}: {
  formulator: FormulatorToolsetBridge;
  sessionId: string;
  startedHere: boolean;
}) {
  const navigate = useNavigate();
  const viewQuery = useWorkItemInterview(sessionId);
  const turn = useFormulatorEvents(sessionId);
  const [phase, setPhase] = useState<"interview" | "review">("interview");
  const [belowBar, setBelowBar] = useState(false);
  const [resumeFailure, setResumeFailure] = useState<FormulatorErrorMessage | null>(null);
  const resumeTried = useRef(false);
  const view = viewQuery.data;

  const resume = useCallback(() => {
    setResumeFailure(null);
    void formulator
      .resume(sessionId)
      .then((result) => {
        if (!result.ok) setResumeFailure(formulatorErrorMessage(result.errorCode, result.message));
      })
      .catch(() => {
        setResumeFailure(formulatorErrorMessage("UNKNOWN"));
      });
  }, [formulator, sessionId]);

  // A session that still awaits Claude and was not started by this page (resumed
  // from the list, reopened after a restart, started on another computer) has
  // nothing running here: run its pending turn on this machine. A turn already
  // running here answers BUSY, which the turn's own progress events then clear.
  useEffect(() => {
    if (!view || startedHere || resumeTried.current) return;
    if (view.awaiting !== "claude" || view.status !== "active") return;
    resumeTried.current = true;
    resume();
  }, [resume, startedHere, view]);

  // Progress for this session means a turn is running here after all.
  useEffect(() => {
    if (turn.running) setResumeFailure(null);
  }, [turn.running]);

  if (viewQuery.isPending) {
    return (
      <Centered>
        <Waiting label="Opening the interview" />
      </Centered>
    );
  }

  if (viewQuery.isError || !view) {
    const patError = getAdoPatErrorCode(viewQuery.error);
    if (patError) {
      return (
        <Centered>
          <AdoPatCallout code={patError} />
        </Centered>
      );
    }
    const notFound = getApiErrorCode(viewQuery.error) === "NOT_FOUND";
    return (
      <Centered>
        <FormulatorNotice
          message={{
            text: notFound
              ? "This interview no longer exists. It may have expired or been finished."
              : "The interview could not be loaded.",
            setup: false,
          }}
          testId="session-load-error"
        >
          {!notFound && (
            <button
              type="button"
              className="font-medium text-link hover:underline"
              onClick={() => {
                void viewQuery.refetch();
              }}
            >
              Try again
            </button>
          )}
          <Link to={FORMULATOR_PATH} className="font-medium text-link hover:underline">
            Start a new one
          </Link>
        </FormulatorNotice>
      </Centered>
    );
  }

  const openImprove = (itemId: string) => {
    void navigate(formulatorImprovePath(view.container, itemId));
  };

  if (phase === "review") {
    return (
      <FormulatorReview
        sessionId={sessionId}
        turn={turn}
        onKeepEditing={() => {
          setPhase("interview");
        }}
        onBelowBar={() => {
          setBelowBar(true);
          setPhase("interview");
        }}
        onRestartImprove={openImprove}
      />
    );
  }

  return (
    <FormulatorInterview
      formulator={formulator}
      view={view}
      turn={turn}
      activityFallback={DEFAULT_FORMULATOR_ACTIVITY}
      resumeFailure={resumeFailure}
      onRetryClaude={resume}
      belowBar={belowBar}
      onReview={() => {
        setBelowBar(false);
        setPhase("review");
      }}
      onImproveInstead={openImprove}
    />
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function FormulatorFlow({ formulator }: { formulator: FormulatorToolsetBridge }) {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionId = params.get("session")?.trim() ?? "";
  const mode = params.get("mode");
  const project = params.get("project")?.trim() ?? "";
  const id = params.get("id")?.trim() ?? "";
  const startedHere = (location.state as FormulatorLocationState | null)?.startedHere === true;

  const openSession = useCallback(
    (started: string, options: { replace?: boolean } = {}) => {
      const state: FormulatorLocationState = { startedHere: true };
      void navigate(formulatorSessionPath(started), { replace: options.replace ?? false, state });
    },
    [navigate]
  );
  const openImproveSession = useCallback(
    (started: string) => {
      openSession(started, { replace: true });
    },
    [openSession]
  );

  if (sessionId !== "") {
    return <FormulatorSession key={sessionId} formulator={formulator} sessionId={sessionId} startedHere={startedHere} />;
  }

  if (mode === "improve") {
    if (project !== "" && ITEM_ID_PATTERN.test(id)) {
      return (
        <ImproveStarter
          key={location.key}
          formulator={formulator}
          project={project}
          id={id}
          onStarted={openImproveSession}
        />
      );
    }
    return (
      <Centered>
        <FormulatorNotice
          message={{
            text: "This link does not name an item to improve. Enter the item's ID or ADO link on the start page.",
            setup: false,
          }}
          testId="improve-link-invalid"
        >
          <Button asChild variant="outline" size="sm">
            <Link to={FORMULATOR_PATH}>Go to the start page</Link>
          </Button>
        </FormulatorNotice>
      </Centered>
    );
  }

  return <FormulatorStart key={location.key} formulator={formulator} onStarted={openSession} />;
}

/** `/toolbox/formulator`. */
export function ToolboxFormulatorPage() {
  usePageTitle("ADO Formulator");
  const bridge = useToolboxBridge();
  const support = useToolsetSupport("formulator");
  const formulator = bridge?.formulator ?? null;

  if (!bridge) {
    return (
      <Page>
        <div className="container mx-auto max-w-3xl px-4 py-6">
          <OpenInToolboxCallout reason="The ADO Formulator runs in SpecTree Toolbox: Claude Code reads your project's code on your computer to ask better questions." />
        </div>
      </Page>
    );
  }

  if (support === undefined) {
    return (
      <Page>
        <Centered>
          <Waiting label="Checking SpecTree Toolbox" />
        </Centered>
      </Page>
    );
  }

  if (support !== "supported" || !formulator) {
    return (
      <Page>
        <UpdateNeeded support={support === "spectree-update-needed" ? "spectree-update-needed" : "app-update-needed"} />
      </Page>
    );
  }

  return (
    <Page>
      <FormulatorFlow formulator={formulator} />
    </Page>
  );
}
